import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AppRole, getRolePermissions } from '@/lib/permissions';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: AppRole | null;
  userPermissions: ReturnType<typeof getRolePermissions>;
  loading: boolean;
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  impersonatedUserRole: AppRole | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => void;
  hasPermission: (permission: keyof ReturnType<typeof getRolePermissions>) => boolean;
  getMaxDiscount: () => number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(null);
  const [impersonatedUserRole, setImpersonatedUserRole] = useState<AppRole | null>(null);
  const navigate = useNavigate();

  const userPermissions = getRolePermissions(isImpersonating ? impersonatedUserRole : userRole);

  // Check for impersonation in localStorage on mount
  useEffect(() => {
    const savedImpersonation = localStorage.getItem('impersonation');
    if (savedImpersonation) {
      const { userId, role } = JSON.parse(savedImpersonation);
      setImpersonatedUserId(userId);
      setImpersonatedUserRole(role);
      setIsImpersonating(true);
    }
  }, []);

  const fetchUserRole = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user role:', error);
        return null;
      }

      return data?.role as AppRole | null;
    } catch (error) {
      console.error('Error fetching user role:', error);
      return null;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer role fetching with setTimeout
        if (session?.user) {
          setTimeout(async () => {
            const role = await fetchUserRole(session.user.id);
            setUserRole(role);
            setLoading(false);
          }, 0);
        } else {
          setUserRole(null);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchUserRole(session.user.id).then(role => {
          setUserRole(role);
          setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!error) {
      toast.success('Welcome back!');
    }

    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });

    if (!error) {
      toast.success('Account created successfully!');
    }

    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
    setIsImpersonating(false);
    setImpersonatedUserId(null);
    setImpersonatedUserRole(null);
    localStorage.removeItem('impersonation');
    toast.success('Signed out successfully');
    navigate('/auth');
  };

  const startImpersonation = async (userId: string) => {
    if (!userPermissions?.canImpersonate) {
      toast.error('You do not have permission to impersonate users');
      return;
    }

    try {
      const role = await fetchUserRole(userId);
      
      // Get impersonated user email for logging
      const { data: profileData } = await supabase
        .from('profiles')
        .select('email')
        .eq('id', userId)
        .single();
      
      setImpersonatedUserId(userId);
      setImpersonatedUserRole(role);
      setIsImpersonating(true);
      localStorage.setItem('impersonation', JSON.stringify({ userId, role }));
      
      // Log impersonation start
      await supabase.rpc('log_admin_action', {
        p_action_type: 'impersonation_start',
        p_action_details: {
          impersonated_email: profileData?.email || '',
          timestamp: new Date().toISOString()
        },
        p_impersonated_user_id: userId
      });
      
      toast.success('Started impersonating user');
      navigate('/');
    } catch (error) {
      console.error('Error starting impersonation:', error);
      toast.error('Failed to start impersonation');
    }
  };

  const stopImpersonation = async () => {
    const targetUserId = impersonatedUserId;
    
    setImpersonatedUserId(null);
    setImpersonatedUserRole(null);
    setIsImpersonating(false);
    localStorage.removeItem('impersonation');
    
    // Log impersonation stop
    if (targetUserId) {
      try {
        await supabase.rpc('log_admin_action', {
          p_action_type: 'impersonation_stop',
          p_action_details: {
            timestamp: new Date().toISOString()
          },
          p_impersonated_user_id: targetUserId
        });
      } catch (error) {
        console.error('Error logging impersonation stop:', error);
      }
    }
    
    toast.success('Stopped impersonating');
    navigate('/');
  };

  const hasPermission = (permission: keyof NonNullable<typeof userPermissions>): boolean => {
    if (!userPermissions) return false;
    return userPermissions[permission] === true;
  };

  const getMaxDiscount = (): number => {
    return userPermissions?.maxDiscount || 0;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        userRole,
        userPermissions,
        loading,
        isImpersonating,
        impersonatedUserId,
        impersonatedUserRole,
        signIn,
        signUp,
        signOut,
        startImpersonation,
        stopImpersonation,
        hasPermission,
        getMaxDiscount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
